<script setup lang="ts">
/**
 * /orders —— 订单只读列表(海关/边检视角)。v2 · G2(S-48 按画板重排)。
 *
 * ═══ 这一页**没有金额列**,不是「先不显示」═══
 *
 * 返回体里根本没有那几个字段:GovOrderItem 的 schema 只要了几个状态与区域字段。
 * 要加一列得先改契约,而改契约会让 guards/check-gov-readonly.mjs 当场报红(L0-01、M-07)。
 *
 * ═══ 七列里有三列是画板画的、契约答不出的(见下面那条 @unimplemented)═══
 *
 * 画板 G2 的七列:单号 / 状态 / 路线 / 承运商 / 起飞时间 / 送达时间 / 凭证。
 * 前四列是真的(路线 = 起点区域 → 终点区域,空的画「未标注」);
 * 后三列在列表级**没有来源** —— GovOrderItem 没有任何时刻字段,也没有 proofs。
 * 处置按 AS-080 的先例:每行画「—」+ 一条 @unimplemented 登记,**不逐行拉详情顶替**
 * (那是每页 50 次请求的 N+1)。为什么留列而不是裁列:画板那七个表头是逐字锁定的一部分
 * (design-copy 的 G2 帧),裁掉它们等于把画板组版拆了;代价是三列「—」的观感,
 * 这一条取舍报回给人。
 *
 * ═══ 计数 chips:只有「共 N 单」是真数 ═══
 *
 * 画板头一行是四枚 chips。契约里 GET /api/gov/orders 只认 page / pageSize
 * (没有 status 参数,也没有聚合端点),所以「共 N 单」取返回体的 total(真数),
 * 其余三枚(按状态的分布)画「—」+ 同一条 @unimplemented 里的另一半理由,
 * **不编数** —— 拿一页 50 条里数出来的数说成全量,页面上完全看不出来(同 Y-26 / AS-087)。
 *
 * ═══ 行入口 ═══
 *
 * 画板没有操作列。旧实现有一颗「详情」按钮 —— 现挪到单号那一格(点单号进详情),
 * 与运营端结算页(O2)同一种做法:九列一列不多,入口不丢。
 *
 * 也**没有任何筛选控件** —— 契约里没有过滤参数,做了下拉就得自己在前端过滤,
 * 那是把「全量里的 12 条」显示成「共 12 单」。要按状态筛就先往契约里加参数。
 *
 * 时间走 @sea-drone/shared 的 formatShanghaiMinute(Y-03):
 * 库里存 UTC、给人看的是东八区,在这里手写 toLocaleString 会在容器 TZ=UTC 时
 * 显示早 8 小时的时刻,而页面看上去完全正常。
 */
import { computed, h, onMounted, ref } from 'vue'
import { NButton, useMessage } from 'naive-ui'
import { useRouter } from 'vue-router'
import {
  DataTable,
  ErrorBanner,
  NavIcon,
  PageHeader,
  PanelCard,
  StatusTag,
  statusLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import { api, ApiError, type GovOrderItem } from '../api/client'

const message = useMessage()
const router = useRouter()

const PAGE_SIZE = 50

const orders = ref<GovOrderItem[]>([])
const total = ref(0)
const page = ref(1)
const loading = ref(false)
const lastError = ref<string | null>(null)

const PAGE_NOTES: readonly ScopeNote[] = [
  {
    title: '这一页只有流向与状态',
    body: '本页只有流向与状态 —— 金额字段在契约的返回体里就不存在(N-02 只给订单只读视图与货品照片、说明、数据大屏)。监管端全程只读,写不了任何业务数据(M-07)。',
    refs: ['N-02', 'M-07'],
  },
]

// ── 三列 + 三枚计数 chip 的缺口(画板画了、契约答不出)─────────────
//
// @unimplemented(reason="画板七列里的「起飞时间」「送达时间」「凭证」在 GovOrderItem 里没有列表级来源:契约的列表项只有 orderNo / status / serviceType / cargoCategory / originArea / destinationArea / carrierName / createdAt —— 没有 tookOffAt、没有 deliveredAt,也没有 proofs(凭证 N/4 要拿每行的 proofs 推,而列表返回体里没有)。逐行调详情接口凑这三列是每页 50 次请求的 N+1,不许。按 AS-080 的先例画「—」。要它先给契约的列表项加字段(或加一条按状态的聚合返回)", owner="me", blocks="G2 表格「起飞时间」「送达时间」「凭证」三列")
//
// @unimplemented(reason="画板计数行的三枚 chip(运输中 / 已送达 / 已取消)要按状态取数,而契约的 govListOrders 只有 page / pageSize:没有 status 过滤参数,也没有聚合端点。不许拿「本页 50 条里数出来的数」顶替 —— 那是把一页的数说成全量,页面上完全看不出来(Y-26 / AS-087 的同一口径)。三条一起画「—」;「共 N 单」那枚是真的(GovOrderPage.total)。要它先给契约加按状态的计数或过滤", owner="me", blocks="G2 计数 chips「运输中」「已送达」「已取消」")

/**
 * 七列,逐列的数据源(画板 G2 的表头逐字):
 *   单号 orderNo(点它进详情 —— 画板没有操作列,入口挪到这一格)·
 *   状态 status(StatusTag,中文走 status.ts 单点)· 路线 = 起点区域 → 终点区域
 *   (空画「未标注」;画板画的是锚地/起降场点名,而契约只有区域,见上一条登记)·
 *   承运商 carrierName(空画「—」)·
 *   起飞时间 / 送达时间 / 凭证 **列表级无来源** → 破折号(见上面那条 @unimplemented)。
 */
const columns = [
  {
    title: '单号',
    key: 'orderNo',
    width: 180,
    render: (row: GovOrderItem) =>
      h(
        'button',
        { type: 'button', class: 'order-no', onClick: () => void router.push(`/orders/${row.orderNo}`) },
        row.orderNo,
      ),
  },
  {
    title: '状态',
    key: 'status',
    width: 110,
    render: (row: GovOrderItem) => h(StatusTag, { status: row.status, tone: 'dark', size: 'sm' }),
  },
  {
    title: '路线',
    key: 'route',
    width: 220,
    render: (row: GovOrderItem) => routeText(row),
  },
  { title: '承运商', key: 'carrierName', width: 160, render: (row: GovOrderItem) => row.carrierName ?? '—' },
  // 起飞时间 / 送达时间 / 凭证:契约里没有列表级来源 —— 破折号,理由见上面那条 @unimplemented
  { title: '起飞时间', key: 'tookOffAt', width: 110, render: () => '—' },
  { title: '送达时间', key: 'deliveredAt', width: 110, render: () => '—' },
  { title: '凭证', key: 'proofs', width: 80, render: () => '—' },
]

/** 区域没标注时画「未标注」—— 空着读起来像「这里没有这一项」,它是「客户没填」 */
function routeText(row: GovOrderItem): string {
  return `${row.originArea ?? '未标注'} → ${row.destinationArea ?? '未标注'}`
}

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function refresh(): Promise<void> {
  loading.value = true
  try {
    const result = await api.listOrders({ page: page.value, pageSize: PAGE_SIZE })
    orders.value = result.items
    total.value = result.total
    lastError.value = null
  } catch (e) {
    show(e)
  } finally {
    loading.value = false
  }
}

/** 远端分页:翻页就是再取一次那一页,不在本地切片 —— total 是服务端的数,不是这一页的长度 */
function goPage(next: number): void {
  page.value = next
  void refresh()
}

function rowKey(row: GovOrderItem): string {
  return row.orderNo
}

const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)))

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader tone="dark" title="订单" :notes="PAGE_NOTES">
      <template #actions>
        <!-- 画板没画刷新(现实现的可用件,留着;按其它深色页做成幽灵键) -->
        <button type="button" class="ghost" :disabled="loading" @click="refresh">
          <NavIcon name="refresh" :size="14" />
          刷新
        </button>
      </template>
    </PageHeader>

    <ErrorBanner tone="dark" title="上一次加载失败" :message="lastError" />

    <PanelCard tone="dark" padding="none" glow>
      <!--
        计数行(画板:左四枚 chips、右页码)。三枚状态 chip 画「—」——
        契约里没有按状态的数,理由见源码里那条 @unimplemented;
        状态词一律走 statusLabel 单点(视图里写「运输中」三个字反而是违例)。
      -->
      <div class="bar">
        <div class="chips">
          <span class="chip">共 {{ total }} 单</span>
          <span class="chip chip-transit">{{ statusLabel('in_transit') }} —</span>
          <span class="chip">{{ statusLabel('delivered') }} —</span>
          <span class="chip chip-muted">{{ statusLabel('cancelled') }} —</span>
        </div>
        <span class="page-text">第 {{ page }} / {{ pageCount }} 页</span>
      </div>
      <DataTable
        tone="dark"
        :columns="columns"
        :data="orders"
        :row-key="rowKey"
        :loading="loading"
        :pagination="{ page, pageSize: PAGE_SIZE, itemCount: total, onPage: goPage }"
        empty-text="还没有订单"
      />
    </PanelCard>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 刷新:与其它深色页同一枚幽灵键(无底、1px 描边 border-strong、内边距 [8,14]) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover:not(:disabled) {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

.ghost:disabled {
  opacity: 0.6;
  cursor: default;
}

/* 计数行:chips 在左、页码在右(画板的两端对齐) */
.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 20px;
}

.chips {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* 四枚计数 chip:底 = 面板上一档,圆角 sm,12px(画板指标卡那一族的形态) */
.chip {
  display: inline-flex;
  align-items: center;
  height: 27px;
  padding: 0 10px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text2);
}

/* 「运输中」那枚按画板取绿:它是这一列里唯一「正在跑」的数 */
.chip-transit {
  color: var(--sd-success);
}

.chip-muted {
  color: var(--sd-text3);
}

.page-text {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text3);
}

/* 表格单元格是 render 渲染出来的,拿不到本组件的 scope 属性 —— 用 :deep 穿进去 */
:deep(.order-no) {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-family: var(--sd-font-mono);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
  cursor: pointer;
}

:deep(.order-no:hover) {
  color: var(--sd-brand);
  text-decoration: underline;
}
</style>
